/**
 * useTenant Hook
 *
 * Custom React hook to access the current tenant configuration.
 * This is the primary way to consume tenant data in components.
 *
 * USAGE:
 * ```tsx
 * import { useTenant } from '@/hooks/useTenant';
 *
 * function MyComponent() {
 *   const { tenant, isLoading, error } = useTenant();
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *
 *   return (
 *     <div>
 *       <h1>{tenant.companyName}</h1>
 *       {tenant.featureFlags.enableFaultPipeline && (
 *         <FaultPipelineWidget />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 *
 * @see src/context/TenantContext.tsx for provider implementation
 * @see src/types/tenant.types.ts for type definitions
 * @see CLAUDE.md Architecture Standards for usage rules
 */

import { useContext } from 'react';
import { TenantContext } from '@/context/TenantContext';
import { TenantContextValue, TenantConfig, TenantFeatureFlags } from '@/types/tenant.types';

/**
 * Hook to access the current tenant configuration
 *
 * @throws Error if used outside of TenantProvider
 * @returns TenantContextValue with tenant data, loading state, and error
 *
 * @example
 * // Basic usage
 * const { tenant, isLoading } = useTenant();
 *
 * // With error handling
 * const { tenant, isLoading, error, refreshTenant } = useTenant();
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);

  if (context === undefined) {
    throw new Error(
      'useTenant must be used within a TenantProvider. ' +
      'Make sure your component is wrapped in <TenantProvider> in main.tsx.'
    );
  }

  return context;
}

/**
 * Hook to get a specific tenant property with a fallback value
 *
 * Useful when you need a single property and want to avoid null checks.
 *
 * @param key - The key of the tenant property to retrieve
 * @param fallback - Fallback value if tenant is null or property is undefined
 * @returns The property value or fallback
 *
 * @example
 * const companyName = useTenantProperty('companyName', 'Company');
 * const timezone = useTenantProperty('business.timezone', 'Australia/Sydney');
 */
export function useTenantProperty<K extends keyof TenantConfig>(
  key: K,
  fallback: TenantConfig[K]
): TenantConfig[K] {
  const { tenant } = useTenant();
  return tenant?.[key] ?? fallback;
}

/**
 * Hook to check if a feature is enabled for the current tenant
 *
 * Provides a simple boolean check for feature flags.
 *
 * @param feature - The feature flag key to check
 * @returns true if the feature is enabled, false otherwise
 *
 * @example
 * const isFaultPipelineEnabled = useFeatureFlag('enableFaultPipeline');
 *
 * if (isFaultPipelineEnabled) {
 *   // Show fault pipeline UI
 * }
 */
export function useFeatureFlag(feature: keyof TenantFeatureFlags): boolean {
  const { tenant, isLoading } = useTenant();

  // Return false while loading to prevent flash of content
  if (isLoading || !tenant) {
    return false;
  }

  return tenant.featureFlags[feature] ?? false;
}

/**
 * Hook to get all feature flags for the current tenant
 *
 * @returns TenantFeatureFlags object or null if loading
 *
 * @example
 * const features = useFeatureFlags();
 *
 * if (features?.enableMessaging) {
 *   // Show messaging UI
 * }
 */
export function useFeatureFlags(): TenantFeatureFlags | null {
  const { tenant, isLoading } = useTenant();

  if (isLoading || !tenant) {
    return null;
  }

  return tenant.featureFlags;
}

/**
 * Hook to get the tenant's theme configuration
 *
 * @returns Theme object with color values or null if loading
 *
 * @example
 * const theme = useTenantTheme();
 *
 * // Use in inline styles (prefer Tailwind classes)
 * <div style={{ backgroundColor: theme?.primaryColor }}>
 */
export function useTenantTheme() {
  const { tenant, isLoading } = useTenant();

  if (isLoading || !tenant) {
    return null;
  }

  return tenant.theme;
}

/**
 * Hook to get the tenant's branding assets
 *
 * @returns Branding object with logo URLs or null if loading
 *
 * @example
 * const branding = useTenantBranding();
 *
 * {branding?.logoUrl && (
 *   <img src={branding.logoUrl} alt={tenant.companyName} />
 * )}
 */
export function useTenantBranding() {
  const { tenant, isLoading } = useTenant();

  if (isLoading || !tenant) {
    return null;
  }

  return tenant.branding;
}

/**
 * Hook to get the current tenant ID
 *
 * Useful for Firestore queries that need to filter by tenant.
 *
 * @returns Tenant ID string or null if loading
 *
 * @example
 * const tenantId = useTenantId();
 *
 * // Use in Firestore queries
 * const q = query(
 *   collection(db, 'jobs'),
 *   where('tenantId', '==', tenantId)
 * );
 */
export function useTenantId(): string | null {
  const { tenant, isLoading } = useTenant();

  if (isLoading || !tenant) {
    return null;
  }

  return tenant.id;
}

/**
 * Hook that returns tenant data only when fully loaded
 *
 * This is a stricter version that guarantees tenant is not null.
 * Throws an error if tenant is not available.
 *
 * @throws Error if tenant is null (still loading or error occurred)
 * @returns TenantConfig (guaranteed non-null)
 *
 * @example
 * // Only use this when you're sure tenant will be loaded
 * const tenant = useTenantStrict();
 * console.log(tenant.companyName); // Safe - tenant is guaranteed
 */
export function useTenantStrict(): TenantConfig {
  const { tenant, isLoading, error } = useTenant();

  if (isLoading) {
    throw new Error('Tenant is still loading. Use useTenant() instead and handle isLoading state.');
  }

  if (error) {
    throw new Error(`Tenant failed to load: ${error}`);
  }

  if (!tenant) {
    throw new Error('Tenant is null. Ensure TenantProvider is properly configured.');
  }

  return tenant;
}

// Default export for convenience
export default useTenant;
